import { create } from "zustand";
import { emptyResources, type Resources } from "@empire/shared";
import { useVillageStore } from "./village.js";
import { emptyLayout, hashOf, validateLayout, type Layout } from "@empire/core-sim";
import { LayoutPayload, defaultLayout, fromLayout, toLayout } from "@empire/shared";
import { ApiProblem, Unreachable } from "../api/client.js";
import { fetchLayout, putLayout } from "../api/layout.js";
import { useEditorStore } from "./editor.js";
import { useSessionStore } from "./session.js";

/**
 * Saving the maze (S2-6).
 *
 * "Has this changed since it was saved?" is answered with the SAME fingerprint
 * the server computes — `hashOf` over the normalised sparse payload — rather
 * than by watching for edits. Watching edits sounds simpler and is wrong in
 * both directions: draw a tile and erase it again and an edit-watcher still
 * thinks you have unsaved work, while an edit it failed to notice loses a maze
 * silently. Comparing the thing itself cannot drift, and it means the client
 * and the server agree on what "the same maze" means, which is what makes
 * "already saved" trustworthy.
 */

export function fingerprint(layout: Layout): string {
  return hashOf(fromLayout(layout));
}

/** The fingerprint of a board nobody has touched. */
const EMPTY = fingerprint(emptyLayout());

export type SaveStatus = "idle" | "loading" | "saving";

export interface SaveProblem {
  /** Which of the two failures this is — they need different words and buttons. */
  readonly kind: "offline" | "refused";
  readonly message: string;
}

interface SaveState {
  status: SaveStatus;
  /** Fingerprint of the layout the server is holding, or null if it holds none. */
  savedHash: string | null;
  savedAt: string | null;
  /**
   * The defence the server is holding — always legal. The editor falls back
   * to it when the defender leaves with a board that is not (29 Sep 2026).
   */
  savedLayout: Layout | null;
  problem: SaveProblem | null;
  /** What the village has paid for tower upgrades (D-119); the next save pays the rest. */
  upgradesPaid: Resources;

  /** Pull the saved maze down, if there is one and the board is untouched. */
  load: () => Promise<void>;
  save: () => Promise<void>;
  dismissProblem: () => void;
}

export const useSaveStore = create<SaveState>((set) => ({
  status: "idle",
  savedHash: null,
  savedAt: null,
  savedLayout: null,
  problem: null,
  upgradesPaid: emptyResources(),

  load: async () => {
    const { villageId } = useSessionStore.getState();
    const token = await useSessionStore.getState().token();
    if (!villageId || !token) return;

    set({ status: "loading", problem: null });
    try {
      const response = await fetchLayout(villageId, token);
      if (response.upgradesPaid) set({ upgradesPaid: response.upgradesPaid });

      if (response.savedAt) {
        const layout = toLayout(response.layout);
        const editor = useEditorStore.getState();

        // Still the default road the village was founded with (29 Sep 2026):
        // the maze they built in the tutorial replaces it, if it is legal at
        // the village's real levels (D-112).
        const untouched =
          fingerprint(layout) ===
          fingerprint(
            defaultLayout({
              wallLevel: layout.wallLevel,
              engineersGuildLevel: layout.engineersGuildLevel,
            })
          );
        const kept = untouched ? takeTutorialMaze() : null;
        if (kept) {
          const maze = toLayout({
            ...kept,
            wallLevel: layout.wallLevel,
            engineersGuildLevel: layout.engineersGuildLevel,
          });
          if (validateLayout(maze).ok && fingerprint(editor.layout) === EMPTY) {
            set({ savedHash: fingerprint(layout), savedAt: response.savedAt, savedLayout: layout });
            editor.load(maze);
            set({ status: "idle" });
            await useSaveStore.getState().save();
            return;
          }
        }

        // Never overwrite work. If the defender started drawing while this
        // request was in the air, the saved copy waits rather than wiping it:
        // losing thirty seconds of drawing to a slow network would be the
        // exact failure this story exists to prevent.
        if (fingerprint(editor.layout) === EMPTY) editor.load(layout);

        set({ savedHash: fingerprint(layout), savedAt: response.savedAt, savedLayout: layout });
        forgetTutorialMaze();
      } else {
        set({ savedHash: null, savedAt: null });
        // No defence on the server yet: a new player. The maze they built in
        // the tutorial becomes their first one (D-112), at their village's
        // real levels — if it is legal there. If it is not (it used more of
        // the tutorial's practice Guild than a new village has), they start
        // from the empty board as before rather than from an error.
        const kept = takeTutorialMaze();
        if (kept) {
          const layout = toLayout({
            ...kept,
            wallLevel: response.layout.wallLevel,
            engineersGuildLevel: response.layout.engineersGuildLevel,
          });
          if (
            validateLayout(layout).ok &&
            fingerprint(useEditorStore.getState().layout) === EMPTY
          ) {
            useEditorStore.getState().load(layout);
            set({ status: "idle" });
            await useSaveStore.getState().save();
            return;
          }
        }
      }
      set({ status: "idle" });
    } catch (error) {
      set({ status: "idle", problem: problemFrom(error) });
    }
  },

  save: async () => {
    const layout = useEditorStore.getState().layout;

    // The button is disabled for an illegal maze, but the guard belongs here
    // too: a keyboard shortcut or a future autosave must not be able to send
    // the server something we already know it will refuse.
    if (!validateLayout(layout).ok) return;

    const { villageId } = useSessionStore.getState();
    const token = await useSessionStore.getState().token();
    if (!villageId || !token) {
      set({ problem: { kind: "offline", message: "Not signed in yet." } });
      return;
    }

    set({ status: "saving", problem: null });
    try {
      const response = await putLayout(villageId, token, fromLayout(layout));
      // Trust the server's own fingerprint, not one computed here: if the two
      // ever disagree, the next Save must notice rather than call it clean.
      set({
        status: "idle",
        savedHash: response.hash ?? fingerprint(toLayout(response.layout)),
        savedAt: response.savedAt ?? new Date().toISOString(),
        savedLayout: toLayout(response.layout),
        ...(response.upgradesPaid ? { upgradesPaid: response.upgradesPaid } : {}),
      });
      // Upgrades were paid for out of the village: show the new totals.
      if (response.charged && Object.values(response.charged).some((n) => n > 0)) {
        void useVillageStore.getState().load({ quiet: true });
      }
    } catch (error) {
      set({ status: "idle", problem: problemFrom(error) });
    }
  },

  dismissProblem: () => set({ problem: null }),
}));

/**
 * A refusal from the server on THIS endpoint is close to a bug report.
 *
 * The editor validates with the same function the server does, so a 422 here
 * means the two disagreed — which should be impossible. Saying so plainly beats
 * a generic "could not save": it is the one message that should make somebody
 * look at the code rather than at their maze.
 */
function problemFrom(error: unknown): SaveProblem {
  if (error instanceof Unreachable) {
    return {
      kind: "offline",
      message: "Can't reach the server. Your maze is safe on this device — try again in a moment.",
    };
  }
  if (error instanceof ApiProblem) {
    if (error.status === 422) {
      return {
        kind: "refused",
        message: `The server rejected this maze: ${error.message} (the editor thought it was legal — please tell me about this one.)`,
      };
    }
    return { kind: "refused", message: error.message };
  }
  return { kind: "offline", message: "Something went wrong saving. Nothing was lost." };
}

/** True when the board differs from what the server is holding. */
export function useIsDirty(): boolean {
  const layout = useEditorStore((s) => s.layout);
  const savedHash = useSaveStore((s) => s.savedHash);
  return isDirty(fingerprint(layout), savedHash);
}

export function isDirty(current: string, savedHash: string | null): boolean {
  // Nothing saved yet: an untouched board is not "unsaved work".
  if (savedHash === null) return current !== EMPTY;
  return current !== savedHash;
}

const TUTORIAL_MAZE_KEY = "empire.tutorial.maze";

/** The T2 maze the tutorial left behind, once. */
function takeTutorialMaze(): LayoutPayload | null {
  try {
    const raw = globalThis.localStorage?.getItem(TUTORIAL_MAZE_KEY);
    forgetTutorialMaze();
    if (!raw) return null;
    const parsed = LayoutPayload.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

function forgetTutorialMaze(): void {
  try {
    globalThis.localStorage?.removeItem(TUTORIAL_MAZE_KEY);
  } catch {
    /* nothing to forget */
  }
}
